import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { PhaseRow } from './PhaseRow';

/** The timeline Card: drag hint, month header, phase bars and the legend. */
export function GanttTimeline({
  months,
  totalWeeks,
  hasCustom,
  displayPhases,
  phaseDurations,
  isDragging,
  trackRefs,
  onDragStart,
  onKeyboardDurationChange,
  onReset,
}) {
  const { t } = useTranslation();
  return (
    <Card className="bg-white border-[#DDE1E5] overflow-hidden">
      <CardContent className="p-4 sm:p-6">
        <p className="text-xs mb-3" data-testid="gantt-hint">
          {hasCustom ? (
            <span className="text-[#B7791F]">
              {t('gantt.durationsAdjusted')}{' '}
              <button type="button" className="underline cursor-pointer" data-testid="gantt-inline-reset" onClick={onReset}>
                {t('gantt.resetToDefaults')}
              </button>
            </span>
          ) : (
            <span className="text-[#8A929B]">
              {t('gantt.dragHint', 'Drag the handle at the right edge of any phase bar to adjust its duration.')}
            </span>
          )}
        </p>

        <div className="gantt-plot -mx-1 px-1">
          <div>
            {/* Month header */}
            <div className="flex mb-3 pl-[140px] sm:pl-[168px]">
              {Array.from({ length: months }, (_, i) => (
                <div
                  key={`month-${i + 1}`}
                  className="gantt-month flex-1 text-xs text-[#66707A] font-medium pl-1 border-l border-[#DDE1E5]"
                  style={{ minWidth: 0 }}
                  data-testid={`gantt-month-${i + 1}`}
                >
                  M{i + 1}
                </div>
              ))}
            </div>

            {/* Phase rows */}
            <div className="space-y-2.5">
              {displayPhases.map((phase) => (
                <PhaseRow
                  key={phase.id}
                  phase={phase}
                  totalWeeks={totalWeeks}
                  months={months}
                  isModified={phaseDurations[phase.id] !== undefined}
                  isDraggingRef={isDragging}
                  trackRefs={trackRefs}
                  onDragStart={onDragStart}
                  onKeyboardDurationChange={onKeyboardDurationChange}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-5 pt-4 border-t border-[#DDE1E5] flex flex-wrap gap-3">
          {displayPhases.map((phase) => (
            <div key={phase.id} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: phase.color }} />
              <span className="text-xs text-[#66707A]">{t('calcData.phases.' + phase.id)}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
